/**
 * Inline upload for a reserved attachment slot (ADR-0028 Pattern A),
 * rendered in the Avtal-tab editor directly beneath the boolean field that
 * gates the slot (via `VariableField.attachmentSlotUpload`). Lets the user
 * reserve AND fill a fixed Bilaga without switching to the Bilagor tab —
 * the same slot still appears there, backed by the same `contract_attachments`
 * row, so the two surfaces can't diverge.
 */

import { useRef, useState } from 'react';
import { ExternalLink, Paperclip, Upload, X } from 'lucide-react';
import { tenantApi } from '../api.js';
import { uploadBilaga } from '../lib/bilaga-create.js';
import type { AttachmentRef } from '@hyranai/regta-templates';

export function InlineSlotUpload({
  contractId,
  slotId,
  slotLabel,
  attachments,
  disabled,
  onChanged,
}: {
  contractId: string;
  slotId: string;
  slotLabel: string;
  attachments: ReadonlyArray<AttachmentRef>;
  disabled: boolean;
  onChanged: () => Promise<void>;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const bound = attachments.find((a) => a.slotId === slotId) ?? null;

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    setErr(null);
    try {
      await uploadBilaga(contractId, file, { slotId, displayName: slotLabel });
      await onChanged();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!bound) return;
    setBusy(true);
    setErr(null);
    try {
      await tenantApi.contract_attachments.remove({ id: bound.id });
      await onChanged();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const open = async () => {
    if (!bound) return;
    const r = await tenantApi.contract_attachments.download_url({ id: bound.id });
    window.open(r.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="rounded border border-dashed border-stone-300 bg-stone-50 p-2 text-[11px]">
      {bound ? (
        <div className="flex items-center gap-2">
          <Paperclip className="h-3.5 w-3.5 shrink-0 text-stone-400" />
          <span className="truncate text-stone-700">
            {bound.displayName}
            {bound.number != null && (
              <span className="text-stone-400"> · Bilaga {bound.number}</span>
            )}
          </span>
          <button
            type="button"
            onClick={open}
            className="ml-auto flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-stone-500 hover:bg-stone-200 hover:text-stone-700"
            title="Öppna i ny flik"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Öppna
          </button>
          {!disabled && (
            <button
              type="button"
              onClick={remove}
              disabled={busy}
              className="flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-stone-500 hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
              title="Ta bort planlösningen"
            >
              <X className="h-3.5 w-3.5" />
              Ta bort
            </button>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <Upload className="h-3.5 w-3.5 shrink-0 text-stone-400" />
          <span className="text-stone-500">Ingen fil uppladdad än.</span>
          <button
            type="button"
            disabled={disabled || busy}
            onClick={() => inputRef.current?.click()}
            className="ml-auto shrink-0 rounded border border-stone-300 bg-white px-2 py-0.5 font-medium text-stone-700 hover:bg-stone-100 disabled:opacity-50"
          >
            {busy ? 'Laddar upp…' : 'Ladda upp PDF'}
          </button>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          void pick(f ?? undefined);
        }}
      />
      {err && <p className="mt-1 text-red-700">{err}</p>}
    </div>
  );
}
